/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { ExternalLink, Copy, CheckCircle2, Search, FileText, Globe2 } from "lucide-react";
import { toast } from "sonner";

const SITEMAP_URL = "https://myanimation.fr/api/sitemap.xml";
const ROBOTS_URL = "https://myanimation.fr/robots.txt";
const GSC_URL = "https://search.google.com/search-console";

export default function AdminSEO() {
  const [stats, setStats] = useState({ pages: 0, posts: 0 });

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`)
      .then((r) => r.text())
      .then((xml) => {
        const total = (xml.match(/<loc>/g) || []).length;
        const blogPosts = (xml.match(/\/blog\/[^<]+</g) || []).length;
        setStats({ pages: total - blogPosts, posts: blogPosts });
      })
      .catch(() => {});
  }, []);

  const copy = async (text, label = "URL") => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers / restricted contexts
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`${label} copiée dans le presse-papier.`);
    } catch (e) {
      toast.error("Impossible de copier — sélectionnez et copiez manuellement.");
    }
  };

  return (
    <div data-testid="admin-seo">
      <header className="mb-8">
        <p className="eyebrow">Référencement</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">SEO & <span className="italic gold-text">Search Console.</span></h1>
        <p className="text-white/60 text-sm mt-3 max-w-2xl">
          Tout ce qu'il faut pour que Google indexe vos articles de blog <strong className="text-[#DCB457]">en 24-48h</strong> au lieu de 2-3 semaines.
        </p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        <StatCard icon={Globe2} label="Pages indexables" value={stats.pages} sub="pages statiques (mariage, prestations…)" />
        <StatCard icon={FileText} label="Articles publiés" value={stats.posts} sub="dans le sitemap dynamique" />
        <StatCard icon={Search} label="Sitemap auto" value="LIVE" sub="se met à jour à chaque publication" />
      </div>

      {/* Step 1 */}
      <section className="card-luxe p-7 md:p-9 mb-6" data-testid="seo-step-1">
        <div className="flex items-start gap-4">
          <span className="w-10 h-10 border border-[#DCB457]/40 text-[#DCB457] flex items-center justify-center font-serif-luxe text-2xl shrink-0">1</span>
          <div className="flex-1">
            <h2 className="font-serif-luxe text-2xl">Vérifiez votre sitemap</h2>
            <p className="text-sm text-white/65 mt-2">
              Le sitemap est généré dynamiquement par le serveur. Il inclut <strong>toutes</strong> les pages statiques et <strong>tous</strong> les articles de blog publiés.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <code className="font-mono text-xs bg-black/40 border border-[#DCB457]/20 px-3 py-2 break-all">{SITEMAP_URL}</code>
              <button onClick={() => copy(SITEMAP_URL, "URL du sitemap")} data-testid="seo-copy-sitemap" className="btn-ghost-gold !py-2 !px-3 !text-[11px]">
                <Copy size={12} strokeWidth={1.4} /> Copier
              </button>
              <a href={SITEMAP_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold !py-2 !px-3 !text-[11px]">
                <ExternalLink size={12} strokeWidth={1.4} /> Ouvrir
              </a>
            </div>
            <p className="text-xs text-white/45 mt-3">
              Vous devez voir un fichier XML avec toutes les URLs du site. Si une erreur s'affiche, contactez votre développeur.
            </p>
          </div>
        </div>
      </section>

      {/* Step 2 */}
      <section className="card-luxe p-7 md:p-9 mb-6" data-testid="seo-step-2">
        <div className="flex items-start gap-4">
          <span className="w-10 h-10 border border-[#DCB457]/40 text-[#DCB457] flex items-center justify-center font-serif-luxe text-2xl shrink-0">2</span>
          <div className="flex-1">
            <h2 className="font-serif-luxe text-2xl">Inscrivez-vous à Google Search Console</h2>
            <p className="text-sm text-white/65 mt-2">
              Service <strong>gratuit</strong> de Google qui permet de suivre votre référencement, voir quelles requêtes amènent du trafic, et accélérer l'indexation.
            </p>
            <a href={GSC_URL} target="_blank" rel="noopener noreferrer" data-testid="seo-gsc-link" className="btn-gold mt-5 inline-flex items-center gap-2">
              <ExternalLink size={13} strokeWidth={1.4} /> Ouvrir Search Console
            </a>
            <ol className="mt-5 space-y-2 text-sm text-white/70 list-decimal pl-5">
              <li>Connectez-vous avec votre compte Google (utilisez votre adresse pro si possible).</li>
              <li>Cliquez sur <strong className="text-[#DCB457]">"Ajouter une propriété"</strong>.</li>
              <li>Choisissez <strong className="text-[#DCB457]">"Préfixe d'URL"</strong> et saisissez : <code className="font-mono text-xs">https://myanimation.fr</code></li>
              <li>Validez la propriété — méthode recommandée : <strong>balise HTML</strong> à coller dans le site (demandez-nous, on le fera en 5 min) ou <strong>fichier HTML</strong> à déposer.</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Step 3 */}
      <section className="card-luxe p-7 md:p-9 mb-6" data-testid="seo-step-3">
        <div className="flex items-start gap-4">
          <span className="w-10 h-10 border border-[#DCB457]/40 text-[#DCB457] flex items-center justify-center font-serif-luxe text-2xl shrink-0">3</span>
          <div className="flex-1">
            <h2 className="font-serif-luxe text-2xl">Soumettez le sitemap</h2>
            <p className="text-sm text-white/65 mt-2">
              Une fois la propriété validée, soumettez le sitemap pour que Google trouve <strong className="text-[#DCB457]">tous vos articles en 24-48h</strong>.
            </p>
            <ol className="mt-5 space-y-2 text-sm text-white/70 list-decimal pl-5">
              <li>Dans Search Console, menu de gauche → <strong className="text-[#DCB457]">"Sitemaps"</strong>.</li>
              <li>Dans le champ "Ajouter un sitemap", saisissez : <code className="font-mono text-xs bg-black/40 border border-[#DCB457]/20 px-2 py-1">api/sitemap.xml</code></li>
              <li>Cliquez <strong>"Envoyer"</strong>. Google va parcourir le fichier et indexer toutes les URLs.</li>
            </ol>
            <div className="bg-[#DCB457]/5 border border-[#DCB457]/25 p-4 mt-5 text-sm">
              <p className="text-[#DCB457] uppercase tracking-[0.18em] text-[11px] mb-2 inline-flex items-center gap-2">
                <CheckCircle2 size={13} strokeWidth={1.4} /> Astuce premium
              </p>
              <p className="text-white/75 leading-relaxed">
                Quand vous publiez un nouvel article, retournez dans Search Console → <strong>Inspection d'URL</strong> → collez l'URL de l'article → cliquez <strong>"Demander une indexation"</strong>.
                Google l'indexe alors en <strong className="text-[#DCB457]">2-6 heures</strong> au lieu de 24-48h.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Step 4 */}
      <section className="card-luxe p-7 md:p-9" data-testid="seo-step-4">
        <div className="flex items-start gap-4">
          <span className="w-10 h-10 border border-[#DCB457]/40 text-[#DCB457] flex items-center justify-center font-serif-luxe text-2xl shrink-0">4</span>
          <div className="flex-1">
            <h2 className="font-serif-luxe text-2xl">Suivez vos performances</h2>
            <p className="text-sm text-white/65 mt-2">
              Dans Search Console → <strong className="text-[#DCB457]">"Performances"</strong>, vous voyez :
            </p>
            <ul className="mt-4 space-y-2 text-sm text-white/70 list-none pl-0">
              <li className="pl-4 relative before:content-['—'] before:absolute before:left-0 before:text-[#DCB457]">Les <strong>requêtes</strong> qui amènent du trafic ("DJ mariage Lille", "photobooth Lille", etc.)</li>
              <li className="pl-4 relative before:content-['—'] before:absolute before:left-0 before:text-[#DCB457]">Les <strong>pages</strong> les plus visitées via Google</li>
              <li className="pl-4 relative before:content-['—'] before:absolute before:left-0 before:text-[#DCB457]">Le <strong>CTR</strong> (taux de clic) et la <strong>position moyenne</strong> sur chaque requête</li>
              <li className="pl-4 relative before:content-['—'] before:absolute before:left-0 before:text-[#DCB457]">Les <strong>erreurs d'indexation</strong> (pages refusées par Google) avec explication</li>
            </ul>
            <p className="text-xs text-white/45 mt-5 italic">
              Les premières données apparaissent après ~3-5 jours d'indexation. Patience, le SEO est un marathon, pas un sprint.
            </p>
          </div>
        </div>
      </section>

      {/* Bonus links */}
      <section className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <a href={ROBOTS_URL} target="_blank" rel="noopener noreferrer" className="card-luxe p-5 hover:border-[#DCB457]/45 transition" data-testid="seo-robots-link">
          <p className="eyebrow">Robots.txt</p>
          <p className="font-serif-luxe text-xl mt-2">Vérifier le robots.txt</p>
          <p className="text-xs text-white/55 mt-2">Indique aux moteurs ce qui est crawlable.</p>
        </a>
        <a href="https://www.google.com/business" target="_blank" rel="noopener noreferrer" className="card-luxe p-5 hover:border-[#DCB457]/45 transition" data-testid="seo-gmb-link">
          <p className="eyebrow">Bonus local</p>
          <p className="font-serif-luxe text-xl mt-2">Optimiser Google Business Profile</p>
          <p className="text-xs text-white/55 mt-2">Indispensable pour le SEO local "DJ Lille", "DJ Tourcoing", etc.</p>
        </a>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="card-luxe p-5">
      <div className="flex items-center justify-between">
        <p className="eyebrow">{label}</p>
        <Icon size={18} strokeWidth={1.3} className="text-[#DCB457]" />
      </div>
      <p className="font-serif-luxe text-4xl mt-3">{value}</p>
      <p className="text-[11px] text-white/45 mt-1">{sub}</p>
    </div>
  );
}
